import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { plural } from '../lib/plurales';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  ProcessWithHierarchy,
  ActivityWithTasks,
  JobPositionLegalFields,
  fetchProcessesHierarchy,
  fetchJobPositionsFull,
  createProcess,
  updateProcess,
  deleteProcess,
  reorderProcesses,
  createActivity,
  updateActivity,
  deleteActivity,
  reorderActivities,
} from '../lib/gestion-tecnica-constructor';
import {
  generateFlowDiagramPDF,
  generateJobDescriptionPDF,
  generateRiskMatrixPDF,
  generateRiskMatrixExcel,
} from '../lib/gestion-tecnica-documentos';
import ActivityDetailModal from './ActivityDetailModal';
import './GestionTecnicaConstructorPage.css';

interface JobPositionOption {
  id: string;
  name: string;
}

export default function GestionTecnicaConstructorPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';
  const [processes, setProcesses] = useState<ProcessWithHierarchy[]>([]);
  const [positions, setPositions] = useState<JobPositionOption[]>([]);
  const [positionsFull, setPositionsFull] = useState<JobPositionLegalFields[]>([]);
  const [detailActivityId, setDetailActivityId] = useState<string | null>(null);
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newProcessName, setNewProcessName] = useState('');
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityPositionId, setNewActivityPositionId] = useState('');
  const [processNameError, setProcessNameError] = useState('');
  const [activityNameError, setActivityNameError] = useState('');
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [docResults, setDocResults] = useState<{ diagramaUrl: string | null; descriptivoUrl: string | null; matrizPdfUrl: string | null } | null>(null);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [hierarchy, posRes, posFull] = await Promise.all([
        fetchProcessesHierarchy(companyId),
        supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name'),
        fetchJobPositionsFull(companyId),
      ]);
      setProcesses(hierarchy);
      setPositions((posRes.data ?? []) as JobPositionOption[]);
      setPositionsFull(posFull);
      if (!selectedProcessId && hierarchy.length > 0) {
        setSelectedProcessId(hierarchy[0].id);
      }
    } catch (err: any) {
      setError(err.message ?? 'Error al cargar procesos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedProcess = processes.find(p => p.id === selectedProcessId) ?? null;

  async function handleCreateProcess() {
    if (!newProcessName.trim()) {
      setProcessNameError('Escribe un nombre para el proceso.');
      return;
    }
    if (!companyId) return;
    setProcessNameError('');
    await createProcess(companyId, newProcessName.trim());
    setNewProcessName('');
    await loadAll();
  }

  async function handleRenameProcess(id: string, name: string) {
    await updateProcess(id, { name });
    await loadAll();
  }

  async function handleDeleteProcess(id: string) {
    if (!window.confirm('¿Eliminar este proceso y todas sus actividades y tareas?')) return;
    await deleteProcess(id);
    if (selectedProcessId === id) setSelectedProcessId(null);
    await loadAll();
  }

  async function handleMoveProcess(id: string, direction: -1 | 1) {
    const idx = processes.findIndex(p => p.id === id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= processes.length) return;
    const ordered = [...processes];
    [ordered[idx], ordered[swapIdx]] = [ordered[swapIdx], ordered[idx]];
    await reorderProcesses(ordered.map(p => p.id));
    await loadAll();
  }

  async function handleCreateActivity() {
    if (!newActivityName.trim()) {
      setActivityNameError('Escribe un nombre para la actividad.');
      return;
    }
    if (!companyId || !selectedProcessId) return;
    setActivityNameError('');
    await createActivity(companyId, selectedProcessId, newActivityName.trim(), newActivityPositionId || null);
    setNewActivityName('');
    setNewActivityPositionId('');
    await loadAll();
  }

  async function handleRenameActivity(id: string, name: string) {
    await updateActivity(id, { name });
    await loadAll();
  }

  async function handleActivityPosition(id: string, positionId: string) {
    await updateActivity(id, { position_id: positionId || null });
    await loadAll();
  }

  async function handleDeleteActivity(id: string) {
    if (!window.confirm('¿Eliminar esta actividad y sus tareas?')) return;
    await deleteActivity(id);
    await loadAll();
  }

  async function handleMoveActivity(activities: ActivityWithTasks[], id: string, direction: -1 | 1) {
    const idx = activities.findIndex(a => a.id === id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= activities.length) return;
    const ordered = [...activities];
    [ordered[idx], ordered[swapIdx]] = [ordered[swapIdx], ordered[idx]];
    await reorderActivities(ordered.map(a => a.id));
    await loadAll();
  }

  async function handleGenerateDocs() {
    if (!selectedProcess || !companyId) return;
    setGeneratingDocs(true);
    setDocResults(null);
    try {
      const companyName = (impersonated ?? company)?.name ?? '';
      const diagramaUrl = await generateFlowDiagramPDF(selectedProcess, companyId, companyName, profile?.id ?? null);
      const descriptivoUrl = await generateJobDescriptionPDF(selectedProcess, positionsFull, companyId, companyName, profile?.id ?? null);
      const matrizPdfUrl = await generateRiskMatrixPDF(selectedProcess, companyId, companyName, profile?.id ?? null);
      await generateRiskMatrixExcel(selectedProcess, companyName);
      setDocResults({ diagramaUrl, descriptivoUrl, matrizPdfUrl });
      alert('Documentación generada: GT-01, GT-02 y GT-04 (PDF), y GT-04 (Excel).\n\nSi no ves los 4 archivos descargados, tu navegador pudo haberlos bloqueado. Revisa el ícono de descarga bloqueada en la barra de direcciones (arriba a la derecha) y presiona "Permitir", o revisa tu historial de descargas con Ctrl+J.');
    } catch (err: any) {
      alert('Error al generar documentación: ' + (err.message ?? 'error desconocido'));
    } finally {
      setGeneratingDocs(false);
    }
  }

  if (loading) return <div className="gtc-page"><p>Cargando...</p></div>;

  return (
    <div className="gtc-page">
      <h1>Constructor Inteligente de Gestión Técnica</h1>
      <p className="gtc-subtitle">
        Arma el proceso una vez: Proceso → Actividades → Tareas. GT-01, GT-02 y
        GT-04 se generan desde esta misma captura.
      </p>
      {error && <div className="gtc-error">{error}</div>}

      <div className="gtc-layout">
        <div className="gtc-processes-col">
          <h2>Procesos</h2>
          <div className="gtc-add-row">
            <input
              type="text"
              placeholder="Nombre del proceso (ej. Producción)"
              value={newProcessName}
              onChange={e => { setNewProcessName(e.target.value); setProcessNameError(''); }}
            />
            <button onClick={handleCreateProcess}>+ Agregar</button>
          </div>
          {processNameError && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{processNameError}</span>}
          <ul className="gtc-process-list">
            {processes.map((p, idx) => (
              <li
                key={p.id}
                className={`gtc-process-item ${p.id === selectedProcessId ? 'gtc-active' : ''}`}
              >
                <span onClick={() => setSelectedProcessId(p.id)} className="gtc-process-name">
                  {p.name}
                </span>
                <span className="gtc-row-actions">
                  <button disabled={idx === 0} onClick={() => handleMoveProcess(p.id, -1)}>↑</button>
                  <button disabled={idx === processes.length - 1} onClick={() => handleMoveProcess(p.id, 1)}>↓</button>
                  <button onClick={() => {
                    const name = window.prompt('Nuevo nombre', p.name);
                    if (name && name.trim()) handleRenameProcess(p.id, name.trim());
                  }}>✎</button>
                  <button onClick={() => handleDeleteProcess(p.id)}>🗑</button>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="gtc-activities-col">
          {!selectedProcess && <p>Selecciona o crea un proceso para armar su secuencia de actividades.</p>}
          {selectedProcess && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <h2>Secuencia de actividades — {selectedProcess.name}</h2>
                <button disabled={generatingDocs} onClick={handleGenerateDocs}>
                  {generatingDocs ? 'Generando...' : '📄 Generar documentación'}
                </button>
              </div>
              {positions.length === 0 && (
                <div style={{
                  background: '#78350f', color: '#fde68a', padding: '10px 14px',
                  borderRadius: 8, marginBottom: 14, fontSize: 13,
                }}>
                  ⚠️ Todavía no tienes puestos de trabajo creados. Crea uno primero en{' '}
                  <strong>Puestos de Trabajo</strong> para poder asignarlos a las
                  actividades de este proceso.
                </div>
              )}
              {docResults && (
                <div className="gtc-doc-results">
                  {docResults.diagramaUrl && (
                    <a href={docResults.diagramaUrl} target="_blank" rel="noopener noreferrer">Diagrama de Flujo (GT-01)</a>
                  )}
                  {docResults.descriptivoUrl && (
                    <a href={docResults.descriptivoUrl} target="_blank" rel="noopener noreferrer">Descriptivo de Puestos (GT-02)</a>
                  )}
                  {docResults.matrizPdfUrl && (
                    <a href={docResults.matrizPdfUrl} target="_blank" rel="noopener noreferrer">Matriz de Riesgos — PDF (GT-04)</a>
                  )}
                  <span className="gtc-doc-note">La Matriz de Riesgos también está disponible en Excel (descarga local).</span>
                </div>
              )}
              <div className="gtc-flow">
                {selectedProcess.process_activities.map((a, idx) => (
                  <React.Fragment key={a.id}>
                    <div className="gtc-flow-box">
                      <div className="gtc-flow-box-header">
                        <input
                          type="text"
                          value={a.name}
                          onChange={e => handleRenameActivity(a.id, e.target.value)}
                          onBlur={e => handleRenameActivity(a.id, e.target.value)}
                        />
                        <span className="gtc-row-actions">
                          <button disabled={idx === 0} onClick={() => handleMoveActivity(selectedProcess.process_activities, a.id, -1)}>↑</button>
                          <button disabled={idx === selectedProcess.process_activities.length - 1} onClick={() => handleMoveActivity(selectedProcess.process_activities, a.id, 1)}>↓</button>
                          <button onClick={() => setDetailActivityId(a.id)}>Ver detalle</button>
                          <button onClick={() => handleDeleteActivity(a.id)}>🗑</button>
                        </span>
                      </div>
                      <SelectBuscable
                        ariaLabel={`Puesto de la actividad ${a.name}`}
                        value={a.position_id ?? ''}
                        onChange={v => handleActivityPosition(a.id, v)}
                        vacio="Sin puesto asignado"
                        opciones={positions.map(pos => ({ value: pos.id, label: pos.name }))}
                      />
                      <div className="gtc-task-count">
                        {plural(a.activity_tasks.length, 'tarea', 'tareas')}
                      </div>
                    </div>
                    {idx < selectedProcess.process_activities.length - 1 && (
                      <div className="gtc-flow-arrow">↓</div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <div className="gtc-add-row gtc-add-activity">
                <input
                  type="text"
                  placeholder="Nombre de la actividad (ej. Recepción)"
                  value={newActivityName}
                  onChange={e => { setNewActivityName(e.target.value); setActivityNameError(''); }}
                />
                <SelectBuscable ariaLabel="Puesto de la actividad nueva" value={newActivityPositionId} onChange={setNewActivityPositionId}
                  vacio="Sin puesto asignado" opciones={positions.map(pos => ({ value: pos.id, label: pos.name }))} />
                <button onClick={handleCreateActivity}>+ Agregar actividad</button>
              </div>
              {activityNameError && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{activityNameError}</span>}
            </>
          )}
        </div>
      </div>

      {detailActivityId && selectedProcess && (() => {
        const activity = selectedProcess.process_activities.find(a => a.id === detailActivityId);
        if (!activity) return null;
        const position = activity.position_id ? positionsFull.find(p => p.id === activity.position_id) ?? null : null;
        return (
          <ActivityDetailModal
            companyId={companyId}
            uploadedBy={profile?.id ?? null}
            activity={activity}
            position={position}
            processName={selectedProcess.name}
            onClose={() => setDetailActivityId(null)}
            onChanged={loadAll}
          />
        );
      })()}
    </div>
  );
}
